import React, { useState } from "react";
import styled from "styled-components";
import { useAuthService } from "../../Context/AuthContext";
import { findTranslatedTextForUser } from "../../Helpers/chatHelper/ChatHelper";
import { displayUserAvatar } from "../../Helpers/chatHelper/AvatarHelper";
import { useTranslate } from "@tolgee/react";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import ReactionButton from "../Chat/ReactionButton";
import ReactionsContainer from "../Chat/ReactionsContainer";
import ReplymessageElement from "../Chat/ReplyMessageElement";
import FileMessageComponent from "../Chat/FileMessageComponent";
import moment from "moment";
import Linkify from "linkify-react";
import { downloadFile } from "../../Helpers/downloadHelper";
import { ArrowDownToLine } from "lucide-react";

const CallChatMessage = ({ message, isOwnMessage, callInfo, isConferenceCall, isPanelViewer, handleReactToMessage, handleRemoveOwnMessageReaction, displayRightClickMenu, handleShowFullScreenImg }) => {
  const { user } = useAuthService();
  const { t } = useTranslate();
  const { automaticTranslations } = useGenericComponentsStateService();
  const [showOriginalMessage, setShowOriginalMessage] = useState(false);
  const [showReactionButton, setShowReactionButton] = useState(false);

  const translatedText = findTranslatedTextForUser(
    message.message_translations,
    user
  );

  const getSenderInfo = () => {
    if (message.user_id === user?.id) {
      return user;
    }
    const sender = callInfo?.other_participants?.find(
      (p) => p.id === message.user_id
    );
    if (sender) return sender;
    if (callInfo?.created_by?.id === message.user_id) {
      return callInfo.created_by;
    }
    return message.user || { name: t("general.unknown_user") };
  };

  const senderInfo = getSenderInfo();

  const getAudienceLabel = () => {
    if (!isConferenceCall || message.conference_audience !== "private") return null;
    const isAttendeeQuestion =
      message.user_id === message.conference_recipient_user_id;
    if (isAttendeeQuestion) {
      if (isOwnMessage) return t("conferences.chat_audience_only_panel");
      return t("conferences.chat_audience_private");
    }
    
    if (isPanelViewer && message.conference_recipient_name) {
      return t("conferences.chat_audience_private_to", {
        name: message.conference_recipient_name,
      });
    }
    return t("conferences.chat_audience_private");
  };
  const audienceLabel = getAudienceLabel();

  const formatTime = (timestamp) => {
    return moment(timestamp).format("HH:mm");
  };

  const getLanguageDisplayName = (language) => {
    if (language === "Unrecognizable") return t('chat.unrecognizable');
    if (language.includes(',')) {
      return language.split(',').map(lang => 
        t(`language_names.${lang.trim()}`, lang.trim())
      ).join(', ');
    }
    return t(`language_names.${language}`, language);
  };

  const displayMessageTranslations = (messageTranslations) => {
    if (!messageTranslations || messageTranslations.length === 0) return [];
    let uniqueTranslations = new Map();
    messageTranslations.forEach((item) => {
      if (isOwnMessage) {
        if (
          item.translation_language !== null &&
          item.translation_language !== user.language &&
          item.translation !== "" &&
          item.user_id !== user.id
        ) {
          if (!uniqueTranslations.has(item.translation_language)) {
            uniqueTranslations.set(item.translation_language, item);
          }
        }
      }
    });
    return Array.from(uniqueTranslations.values());
  };

  const hasTranslation = isOwnMessage 
    ? (message.message_translations && message.message_translations.length > 1)
    : (message.original_message && message.original_message.trim().length > 0 && message.original_message !== translatedText?.translation);

  const allParticipants = [
    ...(callInfo?.other_participants || []),
    callInfo?.created_by,
    user
  ].filter(Boolean);

  const hasImage = message.image && message.image.url;
  const hasVideo = message.video && message.video.video_url;
  const hasDocument = message.document && message.document.url;
  const hasText = message.original_message && message.original_message.trim().length > 0;

  const onDownloadClick = (e, url, fileName) => {
    e.stopPropagation();
    downloadFile(url, fileName || "download");
  };

  return (
    <MessageWrapper 
      isOwnMessage={isOwnMessage}
      onMouseEnter={() => setShowReactionButton(true)}
      onMouseLeave={() => setShowReactionButton(false)}
      onContextMenu={(e) => displayRightClickMenu && displayRightClickMenu(e, message.id, message.conversation_id, message)}
    >
      {!isOwnMessage && (
        <Avatar>{displayUserAvatar(senderInfo, true)}</Avatar>
      )}
      
      <MessageContent isOwnMessage={isOwnMessage}>
        {!isOwnMessage && (
          <SenderName>{senderInfo.name}</SenderName>
        )}
        <MessageBubbleWrapper>
          <MessageBubble isOwnMessage={isOwnMessage} hasMedia={hasImage || hasVideo || hasDocument}>
            {message.message_parent && !message.deleted && (
              <ReplymessageElement
                text={message.message_parent.original_message}
                image={message.message_parent.image}
                file={message.message_parent.document}
                video={message.message_parent.video}
                messageDeleted={message.message_parent.deleted}
                sender={message.message_parent.user?.name || t("general.unknown_user")}
                received={!isOwnMessage}
              />
            )}

            {hasImage && !message.deleted && (
              <ImageWrapper 
                onClick={() => handleShowFullScreenImg && handleShowFullScreenImg(message.image.url, message.id, message.conversation_id)}
              >
                <img src={message.image.url} alt="attachment" />
                <DownloadOverlay 
                    onClick={(e) => onDownloadClick(e, message.image.url, message.image.name || "image.jpg")}
                    title={t("general.download", "Download")}
                >
                    <ArrowDownToLine size={14} />
                </DownloadOverlay>
              </ImageWrapper>
            )}

            {hasVideo && !message.deleted && (
              <VideoWrapper>
                  <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                    <video src={message.video.video_url} controls width="100%">
                    Your browser does not support the video tag.
                    </video>
                    <DownloadOverlay
                        onClick={(e) => onDownloadClick(e, message.video.video_url, "video.mp4")}
                        title={t("general.download", "Download")}
                    >
                        <ArrowDownToLine size={14} />
                    </DownloadOverlay>
                </div>
              </VideoWrapper>
            )}

            {hasDocument && !message.deleted && (
              <FileMessageComponent 
                file={message.document} 
                received={!isOwnMessage}
                onDownload={() => downloadFile(message.document.url, message.document.name)}
              />
            )}

            {message.deleted ? (
              <DeletedText>
                <i>{t("chat.message_deleted")}</i>
              </DeletedText>
            ) : hasText ? (
              <>
                <MessageText isOwnMessage={isOwnMessage} hasMedia={hasImage || hasVideo || hasDocument}>
                  <Linkify options={{ target: "_blank" }}>
                    {isOwnMessage 
                      ? message.original_message
                      : (showOriginalMessage 
                          ? message.original_message 
                          : (translatedText?.translation || message.original_message))
                    }
                  </Linkify>
                </MessageText>

                {isOwnMessage && hasTranslation && automaticTranslations && !isConferenceCall && (
                  <MessageTranslationsWrapper>
                    {displayMessageTranslations(message.message_translations).map((item, index) => (
                      <MessageTranslation key={index}>
                        <MessageTranslationLanguage>
                          {item.translation_language}:{" "}
                        </MessageTranslationLanguage>
                        {item.translation}
                      </MessageTranslation>
                    ))}
                  </MessageTranslationsWrapper>
                )}

                {!isOwnMessage && hasTranslation && automaticTranslations && !isConferenceCall && (
                  <MessageTranslationsWrapper>
                    <MessageTranslation>
                      <MessageTranslationLanguage>
                        {getLanguageDisplayName(message.translated_from_language)}:{" "}
                      </MessageTranslationLanguage>
                      {message.original_message}
                    </MessageTranslation>
                  </MessageTranslationsWrapper>
                )}
              </>
            ) : null}
            {message.reactions && message.reactions.length > 0 && !message.deleted && (
              <ReactionsContainer
                reactions={message.reactions}
                participants={allParticipants}
                adjustmentForSide={isOwnMessage}
                handleRemoveOwnMessageReaction={handleRemoveOwnMessageReaction}
                conversationId={message.conversation_id}
                messageId={message.id}
              />
            )}
          </MessageBubble>
          {showReactionButton && !message.deleted && (
            <ReactionButton
              userId={user.id}
              messageId={message.id}
              conversationId={message.conversation_id}
              reactions={message.reactions || []}
              handleReactToMessage={handleReactToMessage}
              handleRemoveOwnMessageReaction={handleRemoveOwnMessageReaction}
              setShowReactionButton={setShowReactionButton}
            />
          )}
        </MessageBubbleWrapper>
        
        <MessageFooter isOwnMessage={isOwnMessage}>
          <MessageTime isOwnMessage={isOwnMessage}>
            {formatTime(translatedText?.created_at || message.device_sent_date)}
          </MessageTime>

          {audienceLabel && <AudienceTag>🔒 {audienceLabel}</AudienceTag>}

          {!isOwnMessage && !message.deleted && hasTranslation && !automaticTranslations && (
            <ToggleTranslation>
              {showOriginalMessage ? (
                <span onClick={() => setShowOriginalMessage(false)}>
                  {t('chat.preffered_language')}
                </span>
              ) : (
                <>
                  {t('chat.original_in')}{" "}
                  <span onClick={() => setShowOriginalMessage(true)}>
                    {getLanguageDisplayName(message.translated_from_language)}
                  </span>
                </>
              )}
            </ToggleTranslation>
          )}
        </MessageFooter>
      </MessageContent>
    </MessageWrapper>
  );
};

export default CallChatMessage;

const MessageWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-direction: ${(props) => (props.isOwnMessage ? "row-reverse" : "row")};
  padding: 4px 0;
`;

const Avatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  max-width: 32px;
  max-height: 32px;
  border-radius: 100px;
  overflow: hidden;

  > div {
    width: 32px;
    height: 32px;
  }
  
  svg {
    width: 32px;
    height: 32px;
  }
  
  img {
    width: 32px;
    height: 32px;
    object-fit: cover;
    border-radius: 100px;
  }
`;

const MessageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 70%;
  align-items: ${(props) => (props.isOwnMessage ? "flex-end" : "flex-start")};
`;

const SenderName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  padding: 0 8px;
`;

const MessageBubbleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const MessageBubble = styled.div`
  padding: ${props => props.hasMedia ? '4px' : '10px 14px'};
  border-radius: ${(props) =>
    props.isOwnMessage
      ? props.hasMedia ? "8px 8px 4px 8px" : "12px 12px 4px 12px"
      : props.hasMedia ? "8px 8px 8px 4px" : "12px 12px 12px 4px"};
  background: ${({ theme, isOwnMessage }) =>
    isOwnMessage
      ? theme.chat.sentMessageBackground
      : theme.chat.receivedMessageBackground};
  word-wrap: break-word;
  word-break: break-word;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ImageWrapper = styled.div`
  cursor: pointer;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    max-width: 300px;
    height: auto;
    max-height: 300px;
    object-fit: cover;
    display: block;
  }

  &:hover {
    opacity: 0.95;
  }
`;

const DownloadOverlay = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.55);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 999;
  padding: 0;
  outline: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);

  &:hover {
    background-color: rgba(0, 0, 0, 0.75);
    transform: scale(1.1);
  }
`;
const VideoWrapper = styled.div`
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  
  video {
    width: 100%;
    max-width: 300px;
    border-radius: 8px;
  }
`;

const MessageText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: ${({ theme, isOwnMessage }) =>
    isOwnMessage
      ? theme.chat.sentMessageTextColor
      : theme.chat.receivedMessageTextColor};
  margin: 0;
  white-space: pre-wrap;
  padding: ${props => props.hasMedia ? '4px 8px' : '0'};

  a {
    color: ${({ theme, isOwnMessage }) =>
      isOwnMessage
        ? theme.chat.sentMessageTextColor
        : theme.chat.receivedMessageTextColor};
    text-decoration: underline;
  }
`;

const DeletedText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  margin: 0;
`;

const MessageTranslationsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  border-top: 1px solid ${({ theme }) => theme.chat.translationMessageBorderColor};
  padding-top: 8px;
  margin-top: 8px;
`;

const MessageTranslation = styled.p`
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
  color: ${({ theme }) => theme.chat.translationMessageTextColorReceived};
  margin: 0;
  white-space: pre-wrap;
`;

const MessageTranslationLanguage = styled.span`
  font-weight: 600;
`;

const MessageFooter = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  padding: 0 8px;
  flex-direction: ${(props) => (props.isOwnMessage ? "row-reverse" : "row")};
`;

const MessageTime = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme.chat.sentTime};
`;

const AudienceTag = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  opacity: 0.85;
  white-space: nowrap;
`;

const ToggleTranslation = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme.chat.sentTime};
  
  span {
    cursor: pointer;
    font-weight: 600;
    color: ${({ theme }) => theme.chat.readIconColor};
    
    &:hover {
      text-decoration: underline;
    }
  }
`;